import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type {
  HoverIdleSnapshot,
  HoverIdleTimTargetCellSnapshot,
} from "../types";
import { UpgradeInteractionController } from "./upgradeInteractionController";
import { createUpgradeTooltip } from "./upgradeTooltipView";
import { HoverPointerModel } from "../input/hoverPointerModel";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { huge } from "@/shared/numbers";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "./upgradeRepeatTiming";

const COLOR_PROGRESS_MAX_LEVEL = 10;

export type CellUpgradeVariant = "hov" | "tim";

export interface CellUpgradePanelViewOptions {
  readonly variant?: CellUpgradeVariant;
  readonly labelPrefix: "H" | "T";
  readonly costUnit: "Hov" | "Tim";
  readonly gainIncrement: number;
  readonly unlockInitialGain: number;
  readonly gainSuffix: string;
  readonly tooltipRegion: string;
  readonly tooltipSummary: string;
  readonly tooltipAfterLabel: string;
  readonly getGridTier: (snapshot: HoverIdleSnapshot) => number;
  readonly getTargetCells: (
    snapshot: HoverIdleSnapshot,
  ) => readonly HoverIdleTimTargetCellSnapshot[];
  readonly pointerModel: HoverPointerModel;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly onUpgradeCell: (cellIndex: number) => boolean;
}

interface CellItemNodes {
  readonly cellIndex: number;
  readonly wrapper: HTMLDivElement;
  readonly button: HTMLButtonElement;
  readonly costLine: HTMLSpanElement;
  readonly card: HTMLDivElement;
  readonly tooltipRoot: HTMLDivElement;
  readonly tooltipEffectValue: HTMLSpanElement;
  readonly tooltipAfterValue: HTMLSpanElement;
  readonly tooltipCostValue: HTMLSpanElement;
  readonly tooltipStateValue: HTMLSpanElement;
  readonly interaction: UpgradeInteractionController;
}

export class CellUpgradePanelView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly items: readonly CellItemNodes[];
  private readonly options: CellUpgradePanelViewOptions;

  constructor(options: CellUpgradePanelViewOptions) {
    this.options = options;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-cell-upgrade-panel";
    if (options.variant === "tim") {
      this.root.dataset.variant = "tim";
    }
    this.root.hidden = true;

    const items: CellItemNodes[] = [];
    for (let cellIndex = 1; cellIndex <= 8; cellIndex += 1) {
      const cellLabel = `${options.labelPrefix}${cellIndex + 1}G`;
      const tooltipId = `hover-idle-cell-tooltip-${options.variant ?? "hov"}-${cellIndex}`;

      const wrapper = document.createElement("div");
      wrapper.className = "hover-idle-cell-upgrade-item";
      if (options.variant === "tim") {
        wrapper.dataset.variant = "tim";
      }
      wrapper.dataset.cellIndex = String(cellIndex);

      const button = document.createElement("button");
      button.type = "button";
      button.className = "hover-idle-cell-upgrade";
      button.dataset.virtualPointer = "allow";
      if (options.variant === "tim") {
        button.dataset.variant = "tim";
      }
      const costLine = document.createElement("span");
      costLine.className = "hover-idle-cell-upgrade-cost";
      button.append(costLine);

      const card = document.createElement("div");
      card.className = "hover-idle-upgrade-card hover-idle-cell-upgrade-card";
      card.tabIndex = 0;
      card.setAttribute("aria-label", cellLabel);
      card.setAttribute("aria-describedby", tooltipId);
      const cardLabel = document.createElement("span");
      cardLabel.className = "hover-idle-cell-upgrade-label";
      cardLabel.textContent = cellLabel;
      card.append(cardLabel);

      const tooltip = createUpgradeTooltip({
        id: tooltipId,
        title: cellLabel,
        region: options.tooltipRegion,
        summary: options.tooltipSummary,
        afterLabel: options.tooltipAfterLabel,
      });
      tooltip.root.classList.add("hover-idle-cell-upgrade-tooltip");

      const interaction = new UpgradeInteractionController({
        button,
        card,
        tooltip: tooltip.root,
        pointerModel: options.pointerModel,
        initialIntervalMs: options.initialIntervalMs,
        speedMultiplier: options.speedMultiplier,
        onUpgrade: () => options.onUpgradeCell(cellIndex),
      });

      wrapper.append(button, card, tooltip.root);
      this.root.append(wrapper);

      items.push({
        cellIndex,
        wrapper,
        button,
        costLine,
        card,
        tooltipRoot: tooltip.root,
        tooltipEffectValue: tooltip.effectValue,
        tooltipAfterValue: tooltip.afterValue,
        tooltipCostValue: tooltip.costValue,
        tooltipStateValue: tooltip.stateValue,
        interaction,
      });
    }
    this.items = items;
  }

  update(snapshot: HoverIdleSnapshot): void {
    const messages = getMessages();
    const tier = this.options.getGridTier(snapshot);
    const targetCells = this.options.getTargetCells(snapshot);
    const shouldShow = tier >= 4;
    this.root.hidden = !shouldShow;
    if (!shouldShow) return;

    const cu = messages.cellUpgrade;

    for (const nodes of this.items) {
      const visible = nodes.cellIndex < tier;
      nodes.wrapper.hidden = !visible;
      if (!visible) continue;

      const cellSnap = targetCells[nodes.cellIndex];
      if (!cellSnap) continue;

      const unlocked = cellSnap.unlocked;
      const affordable = cellSnap.canPurchase;
      const progress = Math.min(
        cellSnap.purchasedUpgrades / COLOR_PROGRESS_MAX_LEVEL,
        1,
      );
      const affordableStr = affordable ? "true" : "false";

      nodes.button.style.setProperty("--cell-progress", progress.toFixed(3));
      nodes.button.dataset.unlocked = unlocked ? "true" : "false";
      nodes.button.dataset.affordable = affordableStr;
      nodes.button.disabled = !affordable;
      nodes.card.dataset.affordable = affordableStr;
      nodes.tooltipRoot.dataset.affordable = affordableStr;

      const actionPrefix = cu.actionPrefix;
      const costFormatted = formatForContext(
        cellSnap.upgradeCost.ceil(),
        "compact",
      );
      nodes.costLine.textContent = `${actionPrefix} ${cu.upgradeCost(costFormatted, this.options.costUnit)}`;

      const incrementText = formatForContext(
        huge(this.options.gainIncrement),
        "tooltip",
      );
      const initialText = formatForContext(
        huge(this.options.unlockInitialGain),
        "tooltip",
      );
      nodes.tooltipEffectValue.textContent = unlocked
        ? cu.upgradeEffect(incrementText)
        : cu.upgradeEffect(initialText);

      nodes.tooltipAfterValue.textContent = unlocked
        ? cu.upgradeAfter(
            formatForContext(cellSnap.timGainPerSecond, "tooltip"),
            formatForContext(
              cellSnap.timGainPerSecond.add(this.options.gainIncrement),
              "tooltip",
            ),
          )
        : cu.upgradeAfter("0", initialText);

      nodes.tooltipCostValue.textContent = cu.upgradeCost(
        formatForContext(cellSnap.upgradeCost.ceil(), "tooltip"),
        this.options.costUnit,
      );

      nodes.tooltipStateValue.textContent = affordable
        ? cu.upgradeStateReady
        : cu.upgradeStateNeed(
            formatForContext(cellSnap.upgradeCost.ceil(), "tooltip"),
            this.options.costUnit,
          );
    }
  }

  dispose(): void {
    for (const nodes of this.items) nodes.interaction.dispose();
    this.root.remove();
  }
}
